import { GlAlert, GlButton, GlFormCheckbox, GlTooltipDirective } from '../../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import VueDraggable from '../../../lib/utils/vue3compat/draggable_compat.vue?vue3';
import { hasTouchCapability } from '~/lib/utils/touch_detection';
import * as Sentry from '~/sentry/sentry_browser_wrapper';
import { s__ } from '~/locale';
import { validateImageName } from '~/lib/utils/file_upload';
import { isLoggedIn } from '~/lib/utils/common_utils';
import { TYPENAME_DESIGN_VERSION } from '~/graphql_shared/constants';
import { convertToGraphQLId } from '~/graphql_shared/utils';
import { findDesignsWidget, canRouterNav, trackCrudCollapse } from '../../utils.js?vue3';
import CrudComponent from '../../../vue_shared/components/crud_component.vue?vue3';
import DesignDropzone from '../../../vue_shared/components/upload_dropzone/upload_dropzone.vue?vue3';
import { WORK_ITEM_DESIGN_COLLAPSE_TRACKING_ACTION_EXPANDED, WORK_ITEM_DESIGN_COLLAPSE_TRACKING_ACTION_COLLAPSED } from '../../constants';
import { designArchiveError, VALID_DESIGN_FILE_MIMETYPE, ALERT_VARIANTS, MOVE_DESIGN_ERROR } from './constants';
import { updateStoreAfterDesignsArchive, updateDesignsOnStoreAfterReorder } from './cache_updates.js?vue3';
import { findVersionId, moveDesignOptimisticResponse } from './utils.js?vue3';
import getWorkItemDesignListQuery from './graphql/design_collection.query.graphql';
import archiveDesignMutation from './graphql/archive_design.mutation.graphql';
import moveDesignMutation from './graphql/move_design.mutation.graphql';
import Design from './design_item.vue?vue3';
import DesignVersionDropdown from './design_version_dropdown.vue?vue3';
import ArchiveDesignButton from './archive_design_button.vue?vue3';
export default {
    name: 'DesignManagementWidget',
    components: {
        GlAlert,
        GlButton,
        GlFormCheckbox,
        Design,
        DesignVersionDropdown,
        ArchiveDesignButton,
        CrudComponent,
        DesignDropzone,
        VueDraggable
    },
    directives: {
        GlTooltip: GlTooltipDirective
    },
    inject: [
        'fullPath'
    ],
    props: {
        workItemId: {
            type: String,
            required: false,
            default: ''
        },
        workItemIid: {
            type: String,
            required: false,
            default: null
        },
        isSaving: {
            type: Boolean,
            required: false,
            default: false
        },
        uploadError: {
            type: String,
            required: false,
            default: null
        },
        uploadErrorVariant: {
            type: String,
            required: false,
            default: ALERT_VARIANTS.danger
        },
        canReorderDesign: {
            type: Boolean,
            required: false,
            default: false
        },
        canPasteDesign: {
            type: Boolean,
            required: false,
            default: false
        },
        workItemFullPath: {
            type: String,
            required: true
        },
        workItemWebUrl: {
            type: String,
            required: true
        },
        isGroup: {
            type: Boolean,
            required: true
        },
        isBoard: {
            type: Boolean,
            required: false,
            default: false
        },
        canAddDesign: {
            type: Boolean,
            required: false,
            default: false
        },
        canUpdateDesign: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: [
        'dismiss-error',
        'error',
        'upload'
    ],
    apollo: {
        designCollection: {
            query: getWorkItemDesignListQuery,
            variables () {
                return {
                    id: this.workItemId,
                    atVersion: this.designsVersion,
                    useWorkItemFeatures: true
                };
            },
            update (data) {
                const designWidget = findDesignsWidget(data.workItem);
                if (!designWidget || designWidget.designCollection === null) {
                    return null;
                }
                const { copyState } = designWidget.designCollection;
                const designNodes = designWidget.designCollection.designs.nodes;
                const versionNodes = designWidget.designCollection.versions.nodes;
                return {
                    designs: designNodes,
                    copyState,
                    versions: versionNodes
                };
            },
            skip () {
                return !this.workItemId;
            },
            error () {
                this.error = this.$options.i18n.designLoadingError;
            }
        }
    },
    data () {
        return {
            designCollection: null,
            error: null,
            selectedDesigns: [],
            isArchiving: false,
            isDraggingDesign: false,
            reorderedDesigns: null,
            isReorderingInProgress: false,
            dragStartPosition: null
        };
    },
    computed: {
        isLoggedIn () {
            return isLoggedIn();
        },
        isMobile () {
            return hasTouchCapability();
        },
        designs () {
            return this.designCollection?.designs || [];
        },
        allVersions () {
            return this.designCollection?.versions || [];
        },
        hasDesigns () {
            return this.designs.length > 0;
        },
        hasDesignsAndVersions () {
            return this.hasDesigns || this.allVersions.length > 0;
        },
        isCollapsedByDefault () {
            // "All designs archived": versions exist but none are active.
            return this.allVersions.length > 0 && !this.hasDesigns;
        },
        hasSelectedDesigns () {
            return this.selectedDesigns.length > 0;
        },
        hasValidVersion () {
            return this.$route.query.version && this.allVersions && this.allVersions.some((version)=>version.id.endsWith(this.$route.query.version));
        },
        /**
     * This is needed to increase size of dropzone when all
     * designs are archived and only empty state is visible.
     */ crudBodyClass () {
            return this.hasDesigns ? '' : '!gl-m-0';
        },
        designsVersion () {
            return this.hasValidVersion ? convertToGraphQLId(TYPENAME_DESIGN_VERSION, this.$route.query.version) : null;
        },
        isDraggingDisabled () {
            return !this.isLoggedIn || !this.isLatestVersion || !this.canReorderDesign || this.isReorderingInProgress || this.isMobile;
        },
        latestVersionId () {
            const latestVersion = this.allVersions[0];
            return latestVersion && findVersionId(latestVersion.id);
        },
        isLatestVersion () {
            if (this.allVersions.length > 0) {
                return !this.$route.query.version || !this.latestVersionId || this.$route.query.version === this.latestVersionId;
            }
            return true;
        },
        designCollectionQueryBody () {
            return {
                query: getWorkItemDesignListQuery,
                variables: {
                    id: this.workItemId,
                    atVersion: null,
                    useWorkItemFeatures: true
                }
            };
        },
        selectAllButtonText () {
            return this.hasSelectedDesigns ? s__('DesignManagement|Deselect all') : s__('DesignManagement|Select all');
        },
        canUseRouter () {
            return canRouterNav({
                fullPath: this.fullPath,
                webUrl: this.workItemWebUrl,
                isGroup: this.isGroup,
                issueAsWorkItem: !this.isGroup
            }) && !this.isBoard;
        },
        isDesignDetailActive () {
            // eslint-disable-next-line @gitlab/no-hardcoded-urls -- Vue Router path segment comparison, not a navigational URL
            return this.$route.path.includes('/designs/') || this.$route.name === 'design';
        },
        enablePasteOnNoDesign () {
            return !this.hasDesigns && this.canPasteDesign && this.canAddDesign && !this.isDesignDetailActive;
        }
    },
    watch: {
        // Deliberately not `immediate`: attaching on mount makes an invisible, empty
        // widget claim every paste on the page. Hover is the intent signal instead.
        enablePasteOnNoDesign (newVal) {
            if (newVal) {
                this.toggleOnPasteListener();
            } else {
                this.toggleOffPasteListener();
            }
        }
    },
    unmounted () {
        this.toggleOffPasteListener();
    },
    destroyed () {
        this.toggleOffPasteListener();
    },
    methods: {
        dismissError () {
            this.error = undefined;
            this.$emit('dismiss-error');
        },
        isDesignSelected (filename) {
            return this.selectedDesigns.includes(filename);
        },
        checkboxAriaLabel (design) {
            return this.isDesignSelected(design) ? s__('DesignManagement|Unselect the design') : s__('DesignManagement|Select the design');
        },
        openDesignUpload () {
            this.$refs.fileUpload.click();
        },
        onDesignUploadChange (e) {
            this.$emit('upload', e.target.files);
        },
        changeSelectedDesigns (filename) {
            if (this.isDesignSelected(filename)) {
                this.selectedDesigns = this.selectedDesigns.filter((design)=>design !== filename);
            } else {
                this.selectedDesigns.push(filename);
            }
        },
        toggleDesignsSelection () {
            if (this.hasSelectedDesigns) {
                this.selectedDesigns = [];
            } else {
                this.selectedDesigns = this.designs.map((design)=>design.filename);
            }
        },
        async onArchiveDesign () {
            try {
                this.isArchiving = true;
                await this.$apollo.mutate({
                    mutation: archiveDesignMutation,
                    variables: {
                        filenames: this.selectedDesigns,
                        projectPath: this.workItemFullPath,
                        iid: this.workItemIid
                    },
                    update: this.afterArchiveDesign
                });
            } catch (error) {
                this.error = designArchiveError(this.selectedDesigns.length);
                Sentry.captureException(error);
            } finally{
                // display the latest version
                if (this.$route?.query?.version) {
                    this.$router.push({
                        path: this.$route.path,
                        query: {}
                    });
                }
                this.selectedDesigns = [];
                this.isArchiving = false;
            }
        },
        afterArchiveDesign (store, { data: { designManagementDelete } }) {
            updateStoreAfterDesignsArchive(store, designManagementDelete, this.designCollectionQueryBody, this.selectedDesigns);
        },
        designMoveVariables (newIndex, element) {
            const variables = {
                id: element.id
            };
            if (newIndex > 0) {
                variables.previous = this.reorderedDesigns[newIndex - 1].id;
            }
            if (newIndex < this.reorderedDesigns.length - 1) {
                variables.next = this.reorderedDesigns[newIndex + 1].id;
            }
            return variables;
        },
        async onDesignsReorder ({ moved: { newIndex, element } }) {
            this.isReorderingInProgress = true;
            try {
                await this.$apollo.mutate({
                    mutation: moveDesignMutation,
                    variables: this.designMoveVariables(newIndex, element),
                    update: this.afterReorderDesign,
                    optimisticResponse: moveDesignOptimisticResponse(this.reorderedDesigns)
                });
            } catch (error) {
                this.error = MOVE_DESIGN_ERROR;
                Sentry.captureException(error);
            } finally{
                this.isReorderingInProgress = false;
            }
        },
        afterReorderDesign (store, { data: { designManagementMove } }) {
            updateDesignsOnStoreAfterReorder(store, designManagementMove, this.designCollectionQueryBody);
        },
        onDesignMove (designs) {
            this.reorderedDesigns = designs;
        },
        onMouseDown ({ clientX, clientY }) {
            this.dragStartPosition = {
                x: clientX,
                y: clientY
            };
        },
        onPointerUp (event) {
            // Skip `no-drag` elements (i.e. the selection checkbox)
            if (event.target.closest?.('.no-drag')) {
                return;
            }
            const { clientX, clientY } = event;
            const deltaX = this.dragStartPosition ? Math.abs(clientX - this.dragStartPosition.x) : 0;
            const deltaY = this.dragStartPosition ? Math.abs(clientY - this.dragStartPosition.y) : 0;
            // Checking if the mouse movement was below the drag threshold of 5px
            // to treat the event as a click, not a drag
            if (deltaX < 5 && deltaY < 5) {
                // Envoking click event since VueDraggable suppresses it intermittently
                event.target.click();
                this.isDraggingDesign = false;
                // Tackling Chrome specific issues of VueDraggable not propagating
                // the click to children
                if (event.target.tagName === 'OL') {
                    const designTiles = event.target.querySelectorAll('.js-design-tile');
                    // Finding the tile under the <ol> tag pointer
                    const hoveredTile = Array.from(designTiles).find((tile)=>{
                        const { top, left, right, bottom } = tile.getBoundingClientRect();
                        return clientX >= left && clientX <= right && clientY >= top && clientY <= bottom;
                    });
                    if (hoveredTile) {
                        const designLink = hoveredTile.querySelector('.design-list-item');
                        designLink.click();
                    }
                }
            } else {
                this.isDraggingDesign = true;
            }
            this.lastDragPosition = null;
        },
        onDragEnd () {
            // Deffering setting the flag to false so that the tile
            // click is not fired right after drag ends
            setTimeout(()=>{
                this.isDraggingDesign = false;
            }, 100);
        },
        isValidDragDataType (files) {
            return files.some((item)=>this.$options.VALID_DESIGN_FILE_MIMETYPE.mimetype.includes(item.type));
        },
        isEditableTarget (target) {
            return target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLElement && target.isContentEditable;
        },
        onDesignPaste (event) {
            if (!this.canPasteDesign) return;
            // The listener is on `document`, so a paste meant for any editor on the
            // page (comment box, new-item modal, drawer, Duo chat) reaches us too.
            if (this.isEditableTarget(event.target)) return;
            const { clipboardData } = event;
            if (!clipboardData || !clipboardData.files.length) return;
            const files = Array.from(clipboardData.files);
            if (!this.isValidDragDataType(files)) return;
            event.preventDefault();
            // Use a counter for multiple files in the same paste operation of name image.png
            let duplicateCounter = 0;
            files.forEach((file)=>{
                let filename = validateImageName(file);
                // Generate a unique filename for invalid or default names
                if (!filename || filename === 'image.png') {
                    const timestamp = new Date().toISOString().replace(/[T:Z]/g, '_').replace(/\..+/, '');
                    filename = duplicateCounter === 0 ? `image_${timestamp}.png` : `image_${timestamp}_${duplicateCounter}.png`;
                    duplicateCounter += 1;
                }
                // Create and upload the new file
                const newFile = new File([
                    file
                ], filename);
                this.$emit('upload', [
                    newFile
                ]);
            });
        },
        toggleOnPasteListener () {
            if (this.canAddDesign && !this.isDesignDetailActive) {
                document.addEventListener('paste', this.onDesignPaste);
            }
        },
        toggleOffPasteListener () {
            document.removeEventListener('paste', this.onDesignPaste);
        },
        handleCrudCollapsed (collapsed) {
            trackCrudCollapse(collapsed ? WORK_ITEM_DESIGN_COLLAPSE_TRACKING_ACTION_COLLAPSED : WORK_ITEM_DESIGN_COLLAPSE_TRACKING_ACTION_EXPANDED);
        }
    },
    dragOptions: {
        animation: 200,
        ghostClass: 'gl-invisible',
        forceFallback: true,
        fallbackOnBody: true,
        fallbackTolerance: 1,
        tag: 'ol',
        filter: '.no-drag',
        draggable: '.js-design-tile'
    },
    i18n: {
        designLoadingError: s__('DesignManagement|An error occurred while loading designs. Please try again.'),
        archiveDesignText: s__('DesignManagement|Archive selected'),
        allDesignsArchived: s__('DesignManagement|All designs have been archived.'),
        uploadDesignOverlayText: s__('DesignManagement|Drop your images to start the upload.')
    },
    VALID_DESIGN_FILE_MIMETYPE
};
